import { FC, ReactElement, useMemo, useState } from "react";
import { Range } from "react-date-range";
import { format } from "date-fns";
import { CircularProgress, Grid, Stack, Typography, FormControlLabel, Switch } from "@mui/material";

import { DateRangeFilter, SelectClientBanner } from "@/components";
import { DynamicTable } from "@/components";
import { createExclusionsColumns } from "@/columns/COMMON/exclusions";
import { tokens } from "@/tokens";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { Button, Stack as MStack } from "@mui/material";
import { useGetDashboardUpdateDateQuery, useGetPotentialDashboardExclusionSavingsQuery } from "@/services/dashboard/dashboard-api";
import { useAppSelector } from "@/store";
import { formatDateOrTime, getLastYearToDateRange } from "@/utils";
import { roles } from "@/roles";

const PAGE_SIZE = 1000;

const Exclusions: FC = (): ReactElement => {
  const { user } = useAppSelector((state) => state.auth);
  const { selectedClient, areClientsLoading } = useAppSelector(
    (state) => state.client
  );
  const { selectedTenant, areTenantsLoading } = useAppSelector((state) => state.tenantManagement);

  const [currentPage, setCurrentPage] = useState(1);
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [dateRange, setDateRange] = useState<Range>(() => ({
    ...getLastYearToDateRange(),
    key: "selection",
  }));

  const canSelectClients = useMemo(() => {
    const allowedRoles = [roles.SPOWNER, roles.SPPARTNER,roles.TDADMIN] as string[];
    return allowedRoles.includes(user?.userRole ?? "");
  }, [user]);

  const isTDAdminTCD = useMemo(() => {
    return user?.userRole === roles.TDADMIN && selectedTenant?.tenantType === "TRADEDIVE_CLIENT";
  }, [user, selectedTenant]);

  const commonFilters = useMemo(() => {
    return {
      dateFrom: format(dateRange.startDate as Date, "yyyy-MM-dd"),
      dateTo: format(dateRange.endDate as Date, "yyyy-MM-dd"),
      clientId: selectedClient?._id,
      ...(isTDAdminTCD && {
        tenantId: selectedTenant?._id,
      }),
      pageNo: currentPage,
      pageSize: PAGE_SIZE,
    };
  }, [dateRange, selectedClient, currentPage, selectedTenant]);

  const commonOptions = useMemo(() => {
    if (isTDAdminTCD) {
      return {
        skip: !selectedTenant,
        refetchOnMountOrArgChange: true,
      };
    }
    return {
      skip: canSelectClients && !selectedClient,
      refetchOnMountOrArgChange: true,
    };
  }, [canSelectClients, selectedClient, selectedTenant]);

  const { data: updateDate, isFetching: isFetchingDate } =
    useGetDashboardUpdateDateQuery(
      {
        params: {
          clientId: selectedClient?._id,
          ...(isTDAdminTCD && {
            tenantId: selectedTenant?._id,
          }),
        },
      },
      commonOptions
    );

  const {
    data: exclusionsData,
    isLoading: isExclusionsLoading,
    isFetching: isExclusionsFetching,
  } = useGetPotentialDashboardExclusionSavingsQuery(
    {
      dashboardPotential: commonFilters,
    },
    commonOptions
  );

  const isLoading = useMemo(() => {
    return [isFetchingDate, isExclusionsLoading, areClientsLoading, areTenantsLoading].some(Boolean);
  }, [isFetchingDate, isExclusionsLoading, areClientsLoading, areTenantsLoading]);

  const handleDateRangeChange = (newRange: Range) => {
    setDateRange(newRange);
    setCurrentPage(1); // Reset to first page when date range changes
  };

  if (canSelectClients && !selectedClient && !isTDAdminTCD) {
    return (
      <SelectClientBanner/>
    )
  }

  return (
    <Stack spacing={2}>
      <Grid container spacing={2} alignItems="center" justifyContent="space-between">
        <Grid item xs={12} md={6}>
          <Stack>
            <Typography
              variant="h5"
              fontSize={32}
              sx={{ color: tokens.text.primary }}
              lineHeight="38px"
            >
              Section 301 Exclusions
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: tokens.text.muted }}
              lineHeight="22px"
              fontWeight={tokens.fontWeight.normal}
            >
              Last Updated {formatDateOrTime(updateDate ?? new Date(), "both")}
            </Typography>
          </Stack>
        </Grid>
        <Grid item xs={12} md={6}>
          <Stack
            direction="row"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
            sx={{
              justifyContent: "flex-end",
              marginTop: {
                xs: 2,
                sm: 0,
              },
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={showActiveOnly}
                  onChange={(e) => setShowActiveOnly(e.target.checked)}
                />
              }
              label="Show Active Only"
            />
            <DateRangeFilter value={dateRange} onSelectDate={handleDateRangeChange} />
          </Stack>
        </Grid>
      </Grid>

      {isLoading ? (
        <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
          <CircularProgress />
        </Stack>
      ) : (
        <DynamicTable
          data={(showActiveOnly ? (exclusionsData?.data ?? []).filter((d) => d.active) : (exclusionsData?.data ?? []))}
          stickyHeader
          isLoading={isExclusionsFetching}
          columns={createExclusionsColumns()}
          sx={{
            flex: 1,
            "& .MuiTable-root": { borderCollapse: "collapse", overflowY: "auto" },
            "& .MuiTableRow-root:last-of-type .MuiTableCell-root": { borderBottom: "1px solid #e6e6e9 !important" },
          }}
          handlePageChange={(page) => setCurrentPage(page)}
          handleLimitChange={() => {}}
          limit={PAGE_SIZE}
          page={currentPage}
          total={exclusionsData?.meta.countDocs ?? 0}
          hidePagination={false}
          renderFooter={() => (
            <tfoot>
              <tr>
                <td colSpan={9}>
                  <MStack direction="row" alignItems="center" justifyContent="flex-end" sx={{ px: 2, py: 1 }}>
                    {(exclusionsData?.data?.length ?? 0) > 0 && (
                      <Button
                        variant="outlined"
                        startIcon={<FileDownloadIcon />}
                        onClick={() => {
                          const headers = ["HTS Code","99 Code","Current Rate","New Rate","Potential Savings","Status","Description"];
                          const dataset = showActiveOnly ? (exclusionsData?.data ?? []).filter((d) => d.active) : (exclusionsData?.data ?? []);
                          const csvRows = dataset.map((row) => [
                            row.htsCode,
                            row.code99,
                            row.oldDutyRate,
                            row.newDutyRate,
                            `$${row.savings.toLocaleString()}`,
                            row.active ? 'Active' : 'Expired',
                            row.description,
                          ]);
                          const csvContent = [headers.join(","), ...csvRows.map((r) => r.map((c) => `"${c}"`).join(","))].join("\n");
                          const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
                          const link = document.createElement("a");
                          const url = URL.createObjectURL(blob);
                          link.setAttribute("href", url);
                          link.setAttribute("download", "301_exclusions_opportunities.csv");
                          link.style.visibility = "hidden";
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }}
                      >
                        Download CSV
                      </Button>
                    )}
                  </MStack>
                </td>
              </tr>
            </tfoot>
          )}
        />
      )}
    </Stack>
  );
};

export default Exclusions; 

